/* eslint-disable max-len */
import {
  ComponentType,
  ErrorInfo,
  HtmlHTMLAttributes,
  Suspense,
  useState
} from "react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import clsx from "clsx";
import { styled, SxProps } from "@mui/system";
import { CoreLayoutProps } from "ra-core";

import PullToRefresh from "components/PullDownToRefreshWrapper";
import { AppBar as DefaultAppBar, AppBarProps } from "react-admin";
import { Sidebar as DefaultSidebar, SidebarProps } from "react-admin";
import { Menu as DefaultMenu, MenuProps } from "react-admin";
import { Error, ErrorProps } from "react-admin";
import { SkipNavigationButton } from "react-admin";
import { useSidebarState } from "react-admin";
import { Inspector } from "react-admin";
import { Loading } from "react-admin";
import ChoosePlanModalContainer from "./choosePlanModal/ChoosePlanModal";
import * as User from "providers/user";
import EasyCheckout from "./EasyCheckout";
import OnboardingModal from "./OnboardingModal";

export const PullDownToRefreshLayout = (props: LayoutProps) => {
  const {
    appBar: AppBar = DefaultAppBar,
    appBarAlwaysOn,
    children,
    className,
    dashboard,
    error: errorComponent,
    menu: Menu = DefaultMenu,
    sidebar: Sidebar = DefaultSidebar,
    title,
    ...rest
  } = props;

  const [open] = useSidebarState();
  const { permissions, isNewChargeModalOpen, setIsNewChargeModalOpen } =
    User.useUser();
  const [errorInfo, setErrorInfo] = useState<ErrorInfo>();
  const location = useLocation();
  const navigate = useNavigate();

  const shouldForceChoosePlan = Boolean(location.state?.showChoosePlan);
  const [forceChoosePlan, setForceChoosePlan] = useState<boolean>(
    shouldForceChoosePlan
  );

  useEffect(() => {
    if (shouldForceChoosePlan) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!location?.pathname?.includes("Appointment")) {
      localStorage.removeItem("RaStore.Appointment.listParams");
    }
  }, [location?.pathname]);

  const handleError = (error: Error, info: ErrorInfo) => {
    setErrorInfo(info);
  };

  return (
    <Core className={clsx("layout", className)} {...rest}>
      <SkipNavigationButton />
      <div className={LayoutClasses.appFrame}>
        <AppBar open={open} title={title} alwaysOn={appBarAlwaysOn} />
        <main className={LayoutClasses.contentWithSidebar}>
          <Sidebar appBarAlwaysOn={appBarAlwaysOn}>
            <Menu hasDashboard={!!dashboard} />
          </Sidebar>
          <div id="main-content" className={LayoutClasses.content}>
            <ErrorBoundary
              onError={handleError}
              fallbackRender={({ error, resetErrorBoundary }) => (
                <Error
                  error={error}
                  errorComponent={errorComponent}
                  errorInfo={errorInfo}
                  resetErrorBoundary={resetErrorBoundary}
                  title={title}
                />
              )}
            >
              <Suspense fallback={<Loading />}>
                <PullToRefresh>{children}</PullToRefresh>
              </Suspense>
            </ErrorBoundary>
          </div>
          {!permissions?.isCompanyWithoutStatus && forceChoosePlan && (
            <ChoosePlanModalContainer
              open
              onClose={() => setForceChoosePlan(false)}
            />
          )}
          {permissions?.isCompanyWithoutStatus && (
            <ChoosePlanModalContainer open />
          )}
          {isNewChargeModalOpen && (
            <EasyCheckout
              open={true}
              onClose={() => setIsNewChargeModalOpen(false)}
            />
          )}
          {!permissions?.isArtist && <OnboardingModal />}
        </main>
        <Inspector />
      </div>
    </Core>
  );
};

export interface LayoutProps
  extends CoreLayoutProps,
    Omit<HtmlHTMLAttributes<HTMLDivElement>, "title"> {
  appBar?: ComponentType<AppBarProps>;
  appBarAlwaysOn?: boolean;
  className?: string;
  error?: ComponentType<ErrorProps>;
  menu?: ComponentType<MenuProps>;
  sidebar?: ComponentType<SidebarProps>;
  sx?: SxProps;
}

export interface LayoutState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

const PREFIX = "RaLayout";
export const LayoutClasses = {
  appFrame: `${PREFIX}-appFrame`,
  contentWithSidebar: `${PREFIX}-contentWithSidebar`,
  content: `${PREFIX}-content`
};

const Core = styled("div", {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  zIndex: 1,
  minHeight: "100vh",
  backgroundColor: theme.palette.background.default,
  position: "relative",
  minWidth: "fit-content",
  width: "100%",
  color: theme.palette.getContrastText(theme.palette.background.default),

  [`& .${LayoutClasses.appFrame}`]: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    marginTop: theme.spacing(6),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(7)
    }
  },
  //[`& .${LayoutClasses.contentWithSidebar}`]: {
  //display: 'flex',
  //flexGrow: 1,
  //transition: theme.transitions.create('margin', {
  //easing: theme.transitions.easing.easeOut,
  //duration: theme.transitions.duration.enteringScreen,
  //}),
  //},
  [`& .${LayoutClasses.content}`]: {
    backgroundColor: theme.palette.background.default,
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexBasis: 0,
    padding: 0,
    [theme.breakpoints.up("xs")]: {
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1)
    }
  }
}));
